import React, { useRef, useState } from "react";
import { Card } from "primereact/card";
import { ProgressBar } from "primereact/progressbar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Paginator } from "primereact/paginator";
import { Tag } from "primereact/tag";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { useCreditNotes } from "./hooks/useCreditNotes";
import GenerationRunsCard from "./GenerationRunsCard";
import { GenerateAllCreditNotesDialog } from "../components/GenerateAllCreditNotesDialog";
import { CreditNoteValidationButton } from "../components/Validation/CreditNoteValidationButton";

export default function CreditNotesPage() {
  const {
    status, summaries, runs, activeRun, loading, refresh,
    runProgress, summariesPage, summariesLimit, onSummariesPageChange,
    anyGenerating, markSubmitted,
  } = useCreditNotes();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [generateDialogVisible, setGenerateDialogVisible] = useState(false);

  if (loading && !status) {
    return (
      <div className="layout-main">
        <div className="flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
          <ProgressSpinner />
        </div>
      </div>
    );
  }

  const total = runProgress?.total ?? 0;
  const remaining = runProgress?.remaining ?? 0;
  const processed = runProgress?.processed ?? 0;
  const percent = runProgress?.percent ?? 0;
  const hasProcessing = total > 0 && remaining > 0;
  const finished = runProgress?.finished ?? false;
  const startedAtFormatted = runProgress?.startedAt
    ? new Date(runProgress.startedAt).toLocaleString()
    : null;

  const servicePeriodTemplate = (rowData: any) => {
    return `${rowData.service_start_date} - ${rowData.service_end_date}`;
  };

  const totalNetTemplate = (rowData: any) => {
    return `${Number(rowData.total_net).toFixed(2)} EUR`;
  };

  const createdAtTemplate = (rowData: any) => {
    if (!rowData.created_at) return "-";
    const date = new Date(rowData.created_at);
    return date.toLocaleString();
  };

  const claimedAtTemplate = (rowData: any) => {
    if (!rowData.claimed_at) return "-";
    const date = new Date(rowData.claimed_at);
    return date.toLocaleString();
  };

  const easybillStatusTemplate = (rowData: any) => {
    if (rowData.easybill_document_id) {
      return <Tag value={`#${rowData.easybill_document_id}`} severity="success" />;
    }
    return <Tag value="Not submitted" severity="warning" />;
  };

  const subtotalTemplate = (rowData: any) => {
    return `${Number(rowData.subtotal_net).toFixed(2)} EUR`;
  };

  const provisionTemplate = (rowData: any) => {
    return `${Number(rowData.provision_amount).toFixed(2)} EUR`;
  };

  return (
    <div className="layout-main">
      <Toast ref={toast} className="z-10" />
      <GenerateAllCreditNotesDialog
        visible={generateDialogVisible}
        onClose={() => setGenerateDialogVisible(false)}
        toastRef={toast}
        onGenerated={refresh}
        onSubmitted={markSubmitted}
      />

      <div className="surface-section">
        <div className="grid">
          <div className="col-12">
            <div className="flex justify-content-between align-items-center mb-4">
              <h2 className="m-0">Credit Notes Generation and Progress</h2>
              <div className="flex gap-2">
                <Button
                  label="Refresh"
                  icon="pi pi-refresh"
                  severity="secondary"
                  outlined
                  onClick={refresh}
                />
                <Button
                  label="Back to Clearing"
                  icon="pi pi-arrow-left"
                  severity="secondary"
                  outlined
                  onClick={() => navigate("/clearing")}
                />
              </div>
            </div>

            <div className="flex flex-column gap-2 md:flex-row justify-content-start align-items-center mb-4">
              <Button
                className="w-16rem md:my-4 h-4rem"
                label="Generate All Credit Notes"
                icon="pi pi-receipt"
                onClick={() => setGenerateDialogVisible(true)}
                disabled={anyGenerating}
              />

              <CreditNoteValidationButton disabled={anyGenerating} />
            </div>

            <Card className="mb-4">
              {activeRun && (
                <div className="mb-2">
                  <span className="font-semibold">Run {activeRun.month}/{activeRun.year}</span>{" "}
                  <span className="text-color-secondary">
                    — {activeRun.initiatorType === "MANUAL" ? activeRun.initiator : "scheduled"} · {activeRun.status}
                  </span>
                </div>
              )}
              {hasProcessing ? (
                <>
                  {startedAtFormatted && (
                    <div className="mb-3 text-color-secondary">
                      Generation started at {startedAtFormatted}
                    </div>
                  )}
                  <div className="flex justify-content-between align-items-center mb-3">
                    <span className="text-lg font-semibold">
                      {processed} of {total} credit notes processed
                    </span>
                    <span className="text-lg font-bold">{percent}%</span>
                  </div>
                  <ProgressBar value={percent} showValue={false} />
                  <div className="mt-2 text-color-secondary">
                    {remaining} credit note{remaining !== 1 ? "s" : ""} pending submission to EasyBill
                  </div>
                </>
              ) : finished && total > 0 ? (
                <div>
                  <div className="flex align-items-center gap-2">
                    <i className="pi pi-check-circle text-green-500 text-2xl" />
                    <span className="text-lg">
                      All {total} credit notes have been processed
                    </span>
                  </div>
                  {startedAtFormatted && (
                    <div className="mt-2 text-color-secondary">
                      Generation started at {startedAtFormatted} and has finished
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-info-circle text-blue-500 text-2xl" />
                  <span className="text-lg">No credit notes are being processed</span>
                </div>
              )}
            </Card>

            <GenerationRunsCard runs={runs} />

            <Card title="Pending Credit Notes" className="mb-4">
              <DataTable
                value={status?.pending ?? []}
                sortMode="single"
                emptyMessage="No pending credit notes"
                responsiveLayout="scroll"
              >
                <Column field="station_id" header="Station ID" sortable />
                <Column field="customer_name" header="Customer" sortable />
                <Column header="Total Net" body={totalNetTemplate} sortable sortField="total_net" />
                <Column header="Service Period" body={servicePeriodTemplate} sortable sortField="service_start_date" />
                <Column header="Created At" body={createdAtTemplate} sortable sortField="created_at" />
                <Column header="Claimed At" body={claimedAtTemplate} sortable sortField="claimed_at" />
                <Column
                  header="Run"
                  body={(r: any) => (r.process_id ? <span title={r.process_id}>{String(r.process_id).slice(0, 8)}</span> : "—")}
                />
              </DataTable>
            </Card>

            <Card title="Credit Note Summaries (Most Recent)">
              <DataTable
                value={summaries?.data ?? []}
                sortMode="single"
                emptyMessage="No credit note summaries"
                responsiveLayout="scroll"
              >
                <Column field="station_id" header="Station ID" sortable />
                <Column field="customer_name" header="Customer" sortable />
                <Column field="customer_email" header="Email" sortable />
                <Column header="Subtotal" body={subtotalTemplate} sortable sortField="subtotal_net" />
                <Column header="Provision" body={provisionTemplate} sortable sortField="provision_amount" />
                <Column header="Total Net" body={totalNetTemplate} sortable sortField="total_net" />
                <Column header="Service Period" body={servicePeriodTemplate} sortable sortField="service_start_date" />
                <Column header="EasyBill" body={easybillStatusTemplate} sortable sortField="easybill_document_id" />
                <Column header="Created At" body={createdAtTemplate} sortable sortField="created_at" />
              </DataTable>
              {summaries && summaries.total_pages > 1 && (
                <Paginator
                  first={(summariesPage - 1) * summariesLimit}
                  rows={summariesLimit}
                  totalRecords={summaries.total_items}
                  onPageChange={(e) => onSummariesPageChange(Math.floor(e.first / summariesLimit) + 1)}
                  className="mt-3"
                />
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
